import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Box, Typography, CircularProgress, Button, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Card, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useCollection from '../../hooks/useCollection';
import useSearch from '../../hooks/useSearch';
import useViewMode from '../../hooks/useViewMode';
import CollectionHeader from './CollectionHeader';
import AppBarHeader from '../AppBarHeader';
import GridView from './GridView';
import ListView from './ListView';
import TableView from './TableView';
import DetailedView from './DetailedView';
import FixedDetailedView from './FixedDetailedView';
import LargeView from './LargeView';
import ShelfView from './ShelfView';
import ItemDetailDrawer from './ItemDetailDrawer';
import SearchResults from './SearchResults';
import EditDialog from './EditDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import MovieSearch from '../movies/MovieSearch';
import MusicSearch from '../music/MusicSearch';
import { moveToCollection } from '../../utils/wishlistUtils';
import FeaturedCarousel from './FeaturedCarousel';
import SuggestionCarousel from '../suggestions/SuggestionCarousel';
import axiosInstance from '../../axiosInstance';
import BarcodeScanner from '../BarcodeScanner';
import { useCollectionStyle } from '../../hooks/useCollectionStyle';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * Generic collection page component for different collection types
 * @param {Object} props - Component props
 * @param {string} props.collectionType - Type of collection ('games', 'consoles', 'lego', 'movies', 'movie-wishlist')
 * @param {string} props.title - Page title
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @param {Array} props.filterOptions - Options for the filter selector
 * @param {function} props.getFilterValue - Function to get filter value from item
 * @param {Array} props.editFields - Custom fields for the edit dialog
 * @param {boolean} props.isWishlist - Whether this is a wishlist page
 * @param {Object} props.tableColumns - Column definitions for table view
 * @returns {JSX.Element} Collection page component
 */
const CollectionPage = ({
  collectionType,
  title,
  renderProps = {},
  filterOptions = [],
  getFilterValue = (item) => item.platform,
  editFields = [],
  isWishlist = false,
  tableColumns = {},
  customSearchComponent = null,
  itemType = null
}) => {
  const { featureFlags } = useFeatureFlags();
  const { user } = useAuth();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = id || (user ? user.id : null);
  
  // Per-collection style (page + card backgrounds)
  const { pageSx, cardSx } = useCollectionStyle(collectionType.replace('-wishlist', ''));

  // Get singular type for component props and base collection type (for wishlist items)
  const baseCollectionType = collectionType.replace('-wishlist', '');
  
  // Use explicitly passed itemType if provided, otherwise derive it from collectionType
  // Special case for movie-wishlist since the baseCollectionType becomes just 'movie'
  const derivedItemType = collectionType === 'movie-wishlist'
    ? 'movie'
    : baseCollectionType === 'games' 
      ? 'game' 
      : baseCollectionType === 'consoles' 
        ? 'console' 
        : baseCollectionType === 'lego' 
          ? 'lego' 
          : baseCollectionType === 'movies' || baseCollectionType === 'movie'
            ? 'movie'
            : baseCollectionType === 'music'
              ? 'music'
              : baseCollectionType === 'books' || baseCollectionType === 'book'
                ? 'book'
                : baseCollectionType === 'board-games' || baseCollectionType === 'board-game'
                  ? 'board-game'
                  : baseCollectionType === 'trading-cards'
                    ? 'trading-card'
                    : baseCollectionType === 'manga'
                      ? 'manga'
                      : baseCollectionType === 'comics'
                        ? 'comic'
                        : 'item';
            
  // Use the explicitly passed itemType prop if available, otherwise use the derived one
  // Note: Using the destructured prop directly since we're using destructuring in the function parameters
  const finalItemType = itemType || derivedItemType;
  
  // Custom hooks
  const {
    items,
    loading,
    error,
    addItem,
    updateItem,
    deleteItem,
    fetchItems,
    getFilteredItems,
    setFilterPlatform,
    filterStatus,
    setFilterStatus,
    setItems
  } = useCollection(collectionType, userId);

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    loading: searchLoading,
    error: searchError,
    handleSearch: performSearch,
    handleLoadMore: loadMoreResults,
    hasMoreResults,
    isSearching,
    selectedPlatform,
    setSelectedPlatform,
    getFilteredSearchResults,
    searchSource,
    setSearchSource,
  } = useSearch(collectionType, collectionType === 'trading-cards' ? 'pokemon' : 'musicbrainz');
  
  const { viewMode, setViewMode } = useViewMode(collectionType);

  const noShelfTypes = ['trading-cards', 'manga', 'comics'];
  const hideShelf = noShelfTypes.includes(baseCollectionType);
  const noBacklogDNFTypes = ['consoles', 'trading-cards'];

  // If this is a wishlist or a no-shelf type, fall back from shelf to grid
  useEffect(() => {
    if ((isWishlist || hideShelf) && viewMode === 'shelf') {
      setViewMode('grid');
    }
  }, [isWishlist, hideShelf, viewMode, setViewMode]);

  // Local state
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [filter, setFilter] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [movingItem, setMovingItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [barcodeLookupLoading, setBarcodeLookupLoading] = useState(false);
  const [barcodeLookupError, setBarcodeLookupError] = useState('');
  const [barcodeLastDetected, setBarcodeLastDetected] = useState('');
  const [barcodePendingMapping, setBarcodePendingMapping] = useState('');
  const [barcodeDialogPhase, setBarcodeDialogPhase] = useState('scanning');
  const [barcodeLookupResult, setBarcodeLookupResult] = useState(null);
  const barcodeLookupInFlightRef = useRef(false);
  const [barcodeNotFoundSearch, setBarcodeNotFoundSearch] = useState('');
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(() => {
    try { return localStorage.getItem('intercollect_camera_id') || ''; } catch { return ''; }
  });
  const [barcodeSelectedPlatform, setBarcodeSelectedPlatform] = useState('');
  // Track the previous filter value so we can detect genuine changes in the effect below
  const prevFilterRef = useRef('');
  
  // Item detail drawer state
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(24);
  const batchSize = 24;
  const sentinelRef = useRef(null);

  // Sort state — persisted per collection type in localStorage
  const [sortValue, setSortValue] = useState(
    () => localStorage.getItem(`${collectionType}CollectionSortOrder`) || 'alpha_asc'
  );

  // Favourites filter
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  // Sold filter — false = hide sold items (default), true = show sold items only
  const [showSoldOnly, setShowSoldOnly] = useState(false);
  // Backlog filter
  const [showBacklogOnly, setShowBacklogOnly] = useState(false);
  const [showDNFOnly, setShowDNFOnly] = useState(false);

  const isFavourite = (item) => !!(item.favorite || item.favourite || item.isFavorite);

  const sortItems = (arr, sortKey) => {
    const itemsCopy = [...arr];
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const byDate = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    const byYear = (a, b) => (b.year || 0) - (a.year || 0);
    const byRating = (a, b) => (b.rating || 0) - (a.rating || 0);

    switch (sortKey) {
      case 'alpha_asc':
        return itemsCopy.sort(byName);
      case 'alpha_desc':
        return itemsCopy.sort((a, b) => byName(b, a));
      case 'date_desc':
        return itemsCopy.sort(byDate);
      case 'date_asc':
        return itemsCopy.sort((a, b) => -byDate(a, b));
      case 'year_desc':
        return itemsCopy.sort(byYear);
      case 'year_asc':
        return itemsCopy.sort((a, b) => -byYear(a, b));
      case 'rating_desc':
        return itemsCopy.sort(byRating);
      case 'rating_asc':
        return itemsCopy.sort((a, b) => -byRating(a, b));
      case 'favourites_first':
        return itemsCopy.sort((a, b) => {
          const af = isFavourite(a) ? 0 : 1;
          const bf = isFavourite(b) ? 0 : 1;
          if (af !== bf) return af - bf;
          return byName(a, b);
        });
      default:
        return itemsCopy;
    }
  };

  // Load items on component mount
  useEffect(() => {
    if (userId) {
      fetchItems();
    }
  }, [userId, fetchItems]);

  // Refresh items when navigating back/forward to this page so edits made in the details page
  // (e.g., changing imageUrl) are reflected in list/grid/table views.
  useEffect(() => {
    if (userId) {
      fetchItems();
    }
  }, [location.key, userId, fetchItems]);

  // Apply the current favourites + sold + backlog filters on top of a pre-filtered list
  const applyClientFilters = (arr) => {
    let result = arr;
    if (showFavouritesOnly) result = result.filter(isFavourite);
    if (showBacklogOnly) result = result.filter(item => item.inBacklog === true);
    if (showDNFOnly) result = result.filter(item => item.isDNF === true);
    if (showSoldOnly) {
      result = result.filter(item => item.isSold === true);
    } else if (!isWishlist) {
      // Default: hide sold items from the main collection view
      result = result.filter(item => item.isSold !== true);
    }
    return result;
  };

  // Keep backward-compat alias used in a few places below
  const applyFavouritesFilter = applyClientFilters;

  // Update filter and filtered items when filter changes
  useEffect(() => {
    setFilterPlatform(filter);
    const filtered = getFilteredItems();
    setFilteredItems(sortItems(applyClientFilters(filtered), sortValue));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, setFilterPlatform, getFilteredItems]);

  // Update filtered items when status filter changes
  useEffect(() => {
    const filtered = getFilteredItems();
    setFilteredItems(sortItems(applyClientFilters(filtered), sortValue));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, getFilteredItems]);

  // Update filtered items when items change
  useEffect(() => {
    const filtered = getFilteredItems();
    setFilteredItems(sortItems(applyClientFilters(filtered), sortValue));
    // Reset visible items when the source list changes
    setVisibleCount(batchSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, getFilteredItems, collectionType, sortValue]);

  // Re-apply when favourites/sold filter toggles change
  useEffect(() => {
    const filtered = getFilteredItems();
    setFilteredItems(sortItems(applyClientFilters(filtered), sortValue));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFavouritesOnly, showSoldOnly, showBacklogOnly, showDNFOnly]);

  // Apply search query from URL (?search=) for local filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search') || '';
    if (q !== searchQuery) {
      setSearchQuery(q);
      const searchLower = q.toLowerCase();
      const locallyFiltered = getFilteredItems().filter(item => {
        // Reuse same per-type search logic as in handleSearchChange
        if (!q) return true;
        if (collectionType === 'lego') {
          return (
            (item.name && item.name.toLowerCase().includes(searchLower)) || 
            (item.setNum && item.setNum.toLowerCase().includes(searchLower)) ||
            (item.themeName && item.themeName.toLowerCase().includes(searchLower)) ||
            (item.year && item.year.toString().includes(searchLower))
          );
        } else if (collectionType === 'games') {
          return (
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.platform && item.platform.toLowerCase().includes(searchLower)) ||
            (item.year && item.year.toString().includes(searchLower))
          );
        } else if (collectionType === 'consoles') {
          return (
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.manufacturer && item.manufacturer.toLowerCase().includes(searchLower)) ||
            (item.generation && item.generation.toString().includes(searchLower))
          );
        } else if (collectionType === 'movies' || collectionType === 'movie-wishlist') {
          return (
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.format && item.format.toLowerCase().includes(searchLower)) ||
            (item.director && item.director.toLowerCase().includes(searchLower)) ||
            (item.genre && item.genre.toLowerCase().includes(searchLower)) ||
            (item.year && item.year.toString().includes(searchLower))
          );
        } else if (collectionType === 'music') {
          return (
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.artist && item.artist.toLowerCase().includes(searchLower)) ||
            (item.format && item.format.toLowerCase().includes(searchLower)) ||
            (item.genre && item.genre.toLowerCase().includes(searchLower)) ||
            (item.label && item.label.toLowerCase().includes(searchLower)) ||
            (item.year && item.year.toString().includes(searchLower))
          );
        }
        return item.name && item.name.toLowerCase().includes(searchLower);
      });
      setFilteredItems(sortItems(locallyFiltered, sortValue));
      setVisibleCount(batchSize);
    }
  }, [location.search, collectionType, getFilteredItems, sortValue]);

  // Handle search
  const handleSearch = (queryOrEvent) => {
    // Handle both event objects and custom objects with query property
    let actualQuery = '';
    
    if (queryOrEvent && typeof queryOrEvent === 'object') {
      // If it's an event with preventDefault, call it
      if (queryOrEvent.preventDefault) {
        queryOrEvent.preventDefault();
      }
      
      // If it has a query property, use that
      if (queryOrEvent.query !== undefined) {
        actualQuery = queryOrEvent.query;
      } else if (queryOrEvent.target && queryOrEvent.target.value) {
        // If it's an input event
        actualQuery = queryOrEvent.target.value;
      }
    } else if (typeof queryOrEvent === 'string') {
      // If it's a direct string
      actualQuery = queryOrEvent;
    }
    
    // Check if we have a valid query
    if (!actualQuery || !actualQuery.trim()) return;
    
    // Show search results panel
    setShowSearchResults(true);

    performSearch({
      query: actualQuery,
      ...(baseCollectionType === 'games' && filter ? { platform: filter } : {})
    });
  };

  // Reset infinite scroll when filters or search query are cleared/changed in ways that alter the visible set
  useEffect(() => {
    setVisibleCount(batchSize);
  }, [filter, collectionType]);

  const handleSortChange = (val) => {
    setSortValue(val);
    localStorage.setItem(`${collectionType}CollectionSortOrder`, val);
    setFilteredItems((prev) => sortItems(prev, val));
    setVisibleCount(batchSize);
  };

  // IntersectionObserver to grow visibleCount
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (showSearchResults) return; // don't paginate when showing external search results

    const el = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setVisibleCount((prev) => Math.min(prev + batchSize, filteredItems.length));
      }
    }, { root: null, rootMargin: '200px', threshold: 0 });

    observer.observe(el);
    return () => observer.unobserve(el);
  }, [filteredItems.length, showSearchResults]);

  // Dynamically derive filter options from current items or search results for game types.
  // This ensures the platform list always reflects what is actually in the user's
  // collection / current search results rather than a static hardcoded list.
  const effectiveFilterOptions = useMemo(() => {
    if (finalItemType !== 'game') return filterOptions;
    const source = showSearchResults ? searchResults : items;
    if (!source || source.length === 0) return filterOptions;

    const platforms = new Set();
    source.forEach((item) => {
      // Stored wishlist / collection items have a plain `platform` string
      if (item.platform && typeof item.platform === 'string') {
        platforms.add(item.platform);
      }
      // RAWG search results may carry a `platforms` array of objects or strings
      if (Array.isArray(item.platforms)) {
        item.platforms.forEach((p) => {
          const name = typeof p === 'string' ? p : p?.name;
          if (name) platforms.add(name);
        });
      }
    });

    if (platforms.size === 0) return filterOptions;
    return Array.from(platforms)
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({ value: p, label: p }));
  }, [finalItemType, showSearchResults, searchResults, items, filterOptions]);

  // Re-run the external search with the new platform filter whenever it changes while
  // search results are being shown.  Using a ref to detect actual value changes avoids
  // firing on every render and sidesteps any stale-closure issues with event handlers.
  // For wishlists, skip this entirely — platform filtering is done client-side via
  // selectedPlatform / getFilteredSearchResults() to avoid RAWG rate-limit races.
  useEffect(() => {
    if (filter === prevFilterRef.current) return;
    prevFilterRef.current = filter;
    if (!showSearchResults) return;
    if (isWishlist) return;
    if (baseCollectionType !== 'games' && baseCollectionType !== 'game') return;
    const q = searchQuery.trim();
    if (!q) return;
    performSearch({ query: q, platform: filter || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Handle adding item from search results
  const trySaveBarcodeMapping = async (item) => {
    if (!barcodePendingMapping) return;

    const externalSourceMap = {
      game: 'igdb',
      console: 'igdb',
      movie: 'omdb',
      music: 'musicbrainz',
      lego: 'rebrickable'
    };

    try {
      const mappingPayload = {
        code: barcodePendingMapping,
        itemType: finalItemType,
        title: item?.name || item?.title || '',
        platform: item?.platform || item?.format || '',
        externalId: item?.rawgId || item?.igdbId || item?.imdbId || item?.set_num || item?.id || '',
        externalSource: externalSourceMap[finalItemType] || 'unknown'
      };

      const base = typeof axiosInstance.getBaseServerUrl === 'function' ? axiosInstance.getBaseServerUrl() : (axiosInstance.defaults.baseURL || '');
      const mappingUrl = base
        ? `${base}/api/barcodes/mappings`
        : `/barcodes/mappings`;

      await axiosInstance.post(mappingUrl, mappingPayload, {
        timeout: 15000,
        'axios-retry': { retries: 0 }
      });

      setBarcodePendingMapping('');
    } catch (e) {
      console.error('Failed to save barcode mapping:', e);
    }
  };

  const handleAddItem = async (item) => {
    try {
      await addItem(item);

      await trySaveBarcodeMapping(item);

      setShowSearchResults(false);
      setSearchQuery('');
      
      // Refresh collection to show newly added item
      await fetchItems();
      
      setSnackbarMessage(`${finalItemType.charAt(0).toUpperCase() + finalItemType.slice(1)} added successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error adding ${finalItemType}:`, error);
      setSnackbarMessage(`Error adding ${finalItemType}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle opening edit dialog
  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditDialogOpen(true);
  };

  // Handle saving edited item
  const handleSaveEdit = async (updatedItem) => {
    try {
      if (updatedItem?._id) {
        const itemId = updatedItem._id;
        const { _id, ...itemData } = updatedItem;
        await updateItem(itemId, itemData);
        setIsEditDialogOpen(false);
        setSelectedItem(null);
        
        // Show success message
        setSnackbarMessage(`${finalItemType.charAt(0).toUpperCase() + finalItemType.slice(1)} updated successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        await addItem(updatedItem);
        await trySaveBarcodeMapping(updatedItem);
        setIsEditDialogOpen(false);
        setSelectedItem(null);
        setSnackbarMessage(`${finalItemType.charAt(0).toUpperCase() + finalItemType.slice(1)} added successfully`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(`Error updating ${finalItemType}:`, error);
      setSnackbarMessage(`Error updating ${finalItemType}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle rating an item from the detail drawer
  const handleRate = async (item, newRating) => {
    try {
      const { _id, ...itemData } = item;
      await updateItem(_id, { ...itemData, rating: newRating });
      setSelectedDetailItem(prev => prev && prev._id === _id ? { ...prev, rating: newRating } : prev);
    } catch (error) {
      console.error('Error rating item:', error);
    }
  };

  // Handle toggling favourite status
  const handleToggleFavourite = async (item) => {
    try {
      const { _id, ...itemData } = item;
      const newFavouriteStatus = !(item.favorite || item.favourite || item.isFavorite);
      await updateItem(_id, { ...itemData, favourite: newFavouriteStatus });
      // Keep drawer in sync if it's showing this item
      setSelectedDetailItem(prev => prev && prev._id === _id ? { ...prev, favourite: newFavouriteStatus } : prev);
    } catch (error) {
      console.error('Error toggling favourite:', error);
    }
  };

  const handleToggleBacklog = async (item) => {
    try {
      const { _id, ...itemData } = item;
      const newBacklogStatus = !item.inBacklog;
      await updateItem(_id, { ...itemData, inBacklog: newBacklogStatus });
      setSelectedDetailItem(prev => prev && prev._id === _id ? { ...prev, inBacklog: newBacklogStatus } : prev);
    } catch (error) {
      console.error('Error toggling backlog:', error);
    }
  };

  const handleToggleDNF = async (item) => {
    try {
      const { _id, ...itemData } = item;
      const newDNFStatus = !item.isDNF;
      const update = newDNFStatus
        ? { ...itemData, isDNF: true }
        : { ...itemData, isDNF: false, dnfPercent: null, dnfPage: null, dnfChapter: null, dnfTimestamp: null, dnfTrack: null, dnfVolume: null, dnfIssue: null };
      await updateItem(_id, update);
      setSelectedDetailItem(prev => prev && prev._id === _id ? { ...prev, isDNF: newDNFStatus } : prev);
    } catch (error) {
      console.error('Error toggling DNF:', error);
    }
  };

  // Enumerate cameras when the dialog opens (labels only available after getUserMedia permission).
  useEffect(() => {
    if (!barcodeDialogOpen) return;
    const enumerate = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAvailableCameras(devices.filter(d => d.kind === 'videoinput'));
      } catch { /* permissions not yet granted — will retry on next open */ }
    };
    enumerate();
  }, [barcodeDialogOpen]);

  const handleCameraChange = (deviceId) => {
    setSelectedCameraId(deviceId);
    try { localStorage.setItem('intercollect_camera_id', deviceId); } catch {}
  };

  const handleOpenBarcodeScanner = () => {
    setBarcodeLookupError('');
    setBarcodeLastDetected('');
    setBarcodeNotFoundSearch('');
    setBarcodeLookupResult(null);
    setBarcodeDialogPhase('scanning');
    setBarcodeSelectedPlatform('');

    barcodeLookupInFlightRef.current = false;

    setBarcodeDialogOpen(true);
  };

  const handleBarcodeDetected = async (code) => {
    try {
      const trimmed = String(code || '').trim();
      if (!trimmed) return;

      if (barcodeLookupInFlightRef.current) return;
      barcodeLookupInFlightRef.current = true;

      setBarcodeLastDetected(trimmed);
      setBarcodePendingMapping(trimmed);

      setBarcodeLookupResult(null);
      setBarcodeDialogPhase('looking_up');

      setBarcodeLookupLoading(true);
      setBarcodeLookupError('');

      setSnackbarMessage(`Scanned ${trimmed}. Looking up…`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      const base = typeof axiosInstance.getBaseServerUrl === 'function' ? axiosInstance.getBaseServerUrl() : (axiosInstance.defaults.baseURL || '');
      const url = base
        ? `${base}/api/barcodes/${encodeURIComponent(trimmed)}?itemType=${encodeURIComponent(finalItemType)}`
        : `/barcodes/${encodeURIComponent(trimmed)}?itemType=${encodeURIComponent(finalItemType)}`;
      const response = await axiosInstance.get(url, {
        timeout: 20000,
        'axios-retry': { retries: 0 }
      });
      const result = response?.data?.result;
      const item = result?.item;

      barcodeLookupInFlightRef.current = false;

      if (!result) {
        setBarcodeLookupError('No result returned for that barcode');
        setSnackbarMessage('Barcode scanned, but no result was returned');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setBarcodeDialogPhase('not_found');
        return;
      }

      if (!result?.found) {
        setBarcodeLookupError('No product found for that barcode');
        setSnackbarMessage('Scanned barcode, but no product was found. Search and add it once to save a barcode mapping.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setBarcodeDialogPhase('not_found');
        return;
      }

      const inferMovieFormat = (category) => {
        const c = String(category || '').toLowerCase();
        if (c.includes('4k') || c.includes('ultra hd')) return '4K Ultra HD';
        if (c.includes('blu')) return 'Blu-ray';
        if (c.includes('dvd')) return 'DVD';
        if (c.includes('vhs')) return 'VHS';
        return 'DVD';
      };

      // Title is already cleaned server-side (cleanTitle in routes/barcodes.js)
      const cleanedTitle = String(item?.title || '').trim();

      if (!cleanedTitle) {
        setBarcodeLookupError('Barcode matched, but no title was returned');
        setSnackbarMessage('Scanned barcode, but no title was returned');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setBarcodeDialogPhase('not_found');
        return;
      }

      setBarcodeLookupResult({
        code: trimmed,
        cleanedTitle,
        itemType: finalItemType,
        provider: result?.provider || null,
        rawItem: item || null
      });

      setBarcodeDialogPhase('found');
      setBarcodeLookupLoading(false);
      return;

    } catch (error) {
      console.error('Barcode lookup failed:', error);
      setBarcodeLookupError('Lookup failed');
      setSnackbarMessage('Barcode lookup failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setBarcodeDialogPhase('not_found');
    } finally {
      setBarcodeLookupLoading(false);
      barcodeLookupInFlightRef.current = false;
    }
  };

  const handleBarcodeDialogClose = () => {
    setBarcodeDialogOpen(false);
    setBarcodeLookupLoading(false);
    setBarcodeLookupError('');
    setBarcodeLastDetected('');
    setBarcodeNotFoundSearch('');
    setBarcodeLookupResult(null);
    setBarcodeDialogPhase('scanning');
    setBarcodeSelectedPlatform('');
    barcodeLookupInFlightRef.current = false;
  };

  const handleBarcodeUseResult = async () => {
    const cleanedTitle = String(barcodeLookupResult?.cleanedTitle || '').trim();
    const trimmed = String(barcodeLookupResult?.code || '').trim();
    const item = barcodeLookupResult?.rawItem || null;
    if (!cleanedTitle) return;

    const inferMovieFormat = (category) => {
      const c = String(category || '').toLowerCase();
      if (c.includes('4k') || c.includes('ultra hd')) return '4K Ultra HD';
      if (c.includes('blu')) return 'Blu-ray';
      if (c.includes('dvd')) return 'DVD';
      if (c.includes('vhs')) return 'VHS';
      return 'DVD';
    };

    // Games: open EditDialog prefilled with scanned data and selected platform
    if (finalItemType === 'game') {
      handleBarcodeDialogClose();
      
      // Use selected platform from mini-card dropdown, or fall back to detected platform or filter
      const platform = barcodeSelectedPlatform || item?.meta?.platform || filter || 'PC (Microsoft Windows)';
      
      setSelectedItem({
        name: cleanedTitle,
        platform,
        rawgId: item?.meta?.igdb_id || '',
        ...(item?.image ? { imageUrl: item.image } : {}),
        condition: 'Tested & Working',
        edition: 'Standard Edition',
        priority: 'Medium',
        played: false,
        completed: false,
        notes: `Barcode: ${trimmed}`
      });
      setIsEditDialogOpen(true);
      return;
    }

    // Music, lego: search and show results so user picks the right item
    if (finalItemType === 'music' || finalItemType === 'lego') {
      handleBarcodeDialogClose();
      setSearchQuery(cleanedTitle);
      setShowSearchResults(true);
      await performSearch({ query: cleanedTitle });
      setSnackbarMessage(`Searching: "${cleanedTitle}"`);
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }

    // Consoles: pre-fill edit dialog directly (one canonical item per console)
    if (finalItemType === 'console') {
      handleBarcodeDialogClose();
      setSelectedItem({
        name: cleanedTitle,
        ...(item?.image ? { imageUrl: item.image } : {}),
        condition: 'Excellent (EX)',
        priority: 'Medium',
        notes: `Barcode: ${trimmed}`
      });
      setIsEditDialogOpen(true);
      return;
    }

    // Books: pre-fill from ISBN barcode data
    if (finalItemType === 'book') {
      handleBarcodeDialogClose();
      setSelectedItem({
        name: cleanedTitle,
        author: item?.meta?.author || '',
        year: item?.meta?.year || '',
        isbn13: item?.meta?.isbn13 || '',
        isbn10: item?.meta?.isbn10 || '',
        publisher: item?.meta?.publisher || '',
        pages: item?.meta?.pages || '',
        openLibraryKey: item?.meta?.openLibraryKey || '',
        genre: item?.meta?.genre || '',
        series: item?.meta?.series || '',
        seriesNumber: item?.meta?.seriesNumber || '',
        imageUrl: item?.image || '',
        format: 'Paperback',
        read: false,
        notes: `Barcode: ${trimmed}`
      });
      setIsEditDialogOpen(true);
      return;
    }

    // Movies: resolve via OMDB then pre-fill edit dialog
    if (finalItemType === 'movie') {
      handleBarcodeDialogClose();

      const base = typeof axiosInstance.getBaseServerUrl === 'function' ? axiosInstance.getBaseServerUrl() : (axiosInstance.defaults.baseURL || '');
      const omdbUrl = base
        ? `${base}/api/movies/search?query=${encodeURIComponent(cleanedTitle)}`
        : `/movies/search?query=${encodeURIComponent(cleanedTitle)}`;

      const omdbResp = await axiosInstance.get(omdbUrl);
      const results = Array.isArray(omdbResp?.data) ? omdbResp.data : [];
      const best = results[0] || null;

      const imdbId = best?.imdbID || best?.imdbId || null;
      if (!imdbId) {
        setBarcodeLookupError('Barcode matched, but could not resolve an IMDB ID for this movie');
        setSnackbarMessage('Scanned barcode, but could not resolve an IMDB ID');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const poster = best?.Poster && best.Poster !== 'N/A' ? best.Poster : null;
      setSelectedItem({
        name: best?.Title || cleanedTitle,
        imdbId,
        format: inferMovieFormat(item?.category),
        ...(poster ? { imageUrl: poster } : (item?.image ? { imageUrl: item.image } : {})),
        priority: 'Medium',
        watched: false,
        condition: 'Good / Acceptable',
        edition: 'Not Specified',
        year: best?.Year || '',
        director: best?.Director || '',
        genre: best?.Genre || '',
        runtime: best?.Runtime || '',
        notes: `Barcode: ${trimmed}`
      });
      setIsEditDialogOpen(true);
      return;
    }
  };

  const handleBarcodeManualSearch = (queryOverride) => {
    const q = String(queryOverride ?? barcodeNotFoundSearch ?? '').trim();
    handleBarcodeDialogClose();
    setShowSearchResults(true);
    if (q) {
      setSearchQuery(q);
      performSearch({ query: q });
    }
  };

  // Handle opening delete dialog
  const handleDelete = (item) => {
    // ...
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteItem(itemToDelete._id);
        
        setIsDeleteDialogOpen(false);
        setItemToDelete(null);
        
        // Show success message
        setSnackbarMessage(`${finalItemType} removed from ${isWishlist ? 'wishlist' : 'collection'}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Refresh the items list to ensure UI is in sync with backend
        fetchItems();
      } catch (error) {
        console.error(`Error deleting ${finalItemType}:`, error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Show error message
        setSnackbarMessage(`Error removing ${finalItemType}: ${error.message || 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } else {
      console.error('No item to delete - itemToDelete is null or undefined');
    }
  };
  
  // Handle moving item from wishlist to collection
  const handleMoveToCollection = async (item) => {
    if (!isWishlist) return; // Only applicable for wishlist items
    
    setMovingItem(item);
    
    try {
      await moveToCollection(collectionType, item._id);
      
      // Remove the item from the wishlist items
      setItems(prevItems => prevItems.filter(i => i._id !== item._id));
      setFilteredItems(prevFilteredItems => prevFilteredItems.filter(i => i._id !== item._id));
      
      // Show success message
      setSnackbarMessage(`${finalItemType} moved to collection`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error moving ${finalItemType} to collection:`, error);
      
      // Show error message
      setSnackbarMessage(`Error moving ${finalItemType} to collection: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setMovingItem(null);
    }
  };

  // Handle filter change — applies to both the collection view and active search results.
  // When search results are showing, re-runs the external search with the platform
  // constraint so IGDB returns platform-specific results rather than just filtering
  // the already-returned set.
  const handleFilterChange = (event) => {
    const value = event.target.value;
    setFilter(value);
    setFilterPlatform(value);
    setSelectedPlatform(value);
    setFilteredItems(getFilteredItems());
    // The useEffect above picks up the filter change and re-runs the external
    // search when showSearchResults is true, so no explicit call needed here.
  };

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Handle item click from shelf view
  const handleShelfItemClick = (item) => {
    setSelectedDetailItem(item);
    setDetailDrawerOpen(true);
  };

  // Render appropriate view based on view mode
  const renderView = () => {
    // Mark items that are currently being moved
    const itemsWithMovingState = filteredItems.map(item => ({
      ...item,
      moving: movingItem && movingItem._id === item._id
    }));
    
    const pagedItems = itemsWithMovingState.slice(0, visibleCount);
    const viewProps = {
      items: pagedItems,
      onEdit: handleEdit,
      onDelete: handleDelete,
      itemType: finalItemType,
      renderProps,
      isWishlist,
      onMoveToCollection: isWishlist ? handleMoveToCollection : undefined,
      onToggleFavourite: !isWishlist ? handleToggleFavourite : undefined,
      onToggleBacklog: !isWishlist && featureFlags.enableBacklog === true && !noBacklogDNFTypes.includes(baseCollectionType) ? handleToggleBacklog : undefined,
      onToggleDNF: !isWishlist && featureFlags.enableDNF === true && !noBacklogDNFTypes.includes(baseCollectionType) ? handleToggleDNF : undefined,
      cardSx,
    };
    
    switch (viewMode) {
      case 'shelf':
        return (
          <ShelfView
            items={itemsWithMovingState}
            onItemClick={handleShelfItemClick}
            onViewModeChange={handleViewModeChange}
            collectionType={baseCollectionType}
          />
        );
      case 'grid':
        return <GridView {...viewProps} />;
      case 'list':
        return <ListView {...viewProps} />;
      case 'table':
        return <TableView {...viewProps} />;
      case 'detailed':
        return <FixedDetailedView {...viewProps} onUpdateItems={(id, updatedItem) => {
          const updatedItems = items.map(item => 
            item._id === id ? { ...item, ...updatedItem } : item
          );
          setFilterPlatform(filter);
          setFilteredItems(updatedItems.filter(item => {
            // Apply the same filtering logic as in getFilteredItems
            return !filter || getFilterValue(item) === filter;
          }));
        }} />;
      case 'large':
        return <LargeView {...viewProps} />;
      default:
        return <GridView {...viewProps} />;
    }
  };

  // Handle logout function for AppBarHeader
  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  return (
    <div>
      <AppBarHeader 
        isAdmin={user?.isAdmin} 
        onLogout={handleLogout}
      />
      <Container maxWidth="lg">
        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={6000} 
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Box sx={{ mt: 4, mb: 4, ...pageSx }}>
          {(collectionType === 'music') && filter === 'Vinyl' && (
            <Box sx={{ mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={searchSource}
                onChange={(_, v) => { if (v) { setSearchSource(v); setSearchResults([]); } }}
              >
                <ToggleButton value="musicbrainz">MusicBrainz</ToggleButton>
                <ToggleButton value="discogs">Discogs (Vinyl)</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          {collectionType === 'trading-cards' && (
            <Box sx={{ mb: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={searchSource}
                onChange={(_, v) => { if (v) { setSearchSource(v); setSearchResults([]); } }}
              >
                <ToggleButton value="pokemon">Pokémon</ToggleButton>
                <ToggleButton value="mtg">MTG</ToggleButton>
                <ToggleButton value="yugioh">Yu-Gi-Oh!</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          <CollectionHeader
            title={title || `${collectionType.charAt(0).toUpperCase() + collectionType.slice(1)} Collection`}
            searchQuery={searchQuery}
            handleSearchChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              
              // Implement real-time filtering for all collection types
              if (value.trim() === '') {
                // If search is cleared, show all items (filtered by dropdown if applicable)
                setFilteredItems(getFilteredItems());
              } else {
                // Filter items based on search query and collection type
                const filtered = items.filter(item => {
                  const searchLower = value.toLowerCase();
                  
                  if (collectionType === 'lego') {
                    // For Lego items, search across multiple properties
                    return (
                      (item.name && item.name.toLowerCase().includes(searchLower)) || 
                      (item.setNum && item.setNum.toLowerCase().includes(searchLower)) ||
                      (item.themeName && item.themeName.toLowerCase().includes(searchLower)) ||
                      (item.year && item.year.toString().includes(searchLower))
                    );
                  } else if (collectionType === 'games') {
                    // For games, search across name, platform, and year
                    return (
                      (item.name && item.name.toLowerCase().includes(searchLower)) ||
                      (item.platform && item.platform.toLowerCase().includes(searchLower)) ||
                      (item.year && item.year.toString().includes(searchLower))
                    );
                  } else if (collectionType === 'consoles') {
                    // For consoles, search across name, manufacturer, and generation
                    return (
                      (item.name && item.name.toLowerCase().includes(searchLower)) ||
                      (item.manufacturer && item.manufacturer.toLowerCase().includes(searchLower)) ||
                      (item.generation && item.generation.toString().includes(searchLower))
                    );
                  } else if (collectionType === 'movies' || collectionType === 'movie-wishlist') {
                    // For movies, search across name, format, director, genre, and year
                    return (
                      (item.name && item.name.toLowerCase().includes(searchLower)) ||
                      (item.format && item.format.toLowerCase().includes(searchLower)) ||
                      (item.director && item.director.toLowerCase().includes(searchLower)) ||
                      (item.genre && item.genre.toLowerCase().includes(searchLower)) ||
                      (item.year && item.year.toString().includes(searchLower))
                    );
                  } else if (collectionType === 'music') {
                    // For music, search across name, artist, format, genre, label, and year
                    return (
                      (item.name && item.name.toLowerCase().includes(searchLower)) ||
                      (item.artist && item.artist.toLowerCase().includes(searchLower)) ||
                      (item.format && item.format.toLowerCase().includes(searchLower)) ||
                      (item.genre && item.genre.toLowerCase().includes(searchLower)) ||
                      (item.label && item.label.toLowerCase().includes(searchLower)) ||
                      (item.year && item.year.toString().includes(searchLower))
                    );
                  }
                  
                  // Default fallback to name search
                  return item.name && item.name.toLowerCase().includes(searchLower);
                });
                
                setFilteredItems(filtered);
              }
            }}
            handleSearchKeyPress={(e) => e.key === 'Enter' && handleSearch({ preventDefault: () => {}, query: searchQuery })}
            handleSearch={() => handleSearch({ preventDefault: () => {}, query: searchQuery })}
            filterValue={filter}
            handleFilterChange={handleFilterChange}
            filterOptions={effectiveFilterOptions}
            handleClearFilters={() => {
              setFilter('');
              setSearchQuery('');
              setFilterStatus('');
              setShowFavouritesOnly(false);
              setShowBacklogOnly(false);
              setShowDNFOnly(false);
              setFilteredItems(items); // Reset to show all items
            }}
            viewMode={viewMode}
            handleViewModeChange={handleViewModeChange}
            isWishlist={isWishlist}
            hideShelf={hideShelf}
            filterLabel={collectionType === 'lego' || collectionType === 'lego-wishlist' ? 'Filter by Theme' :
              collectionType === 'consoles' || collectionType === 'console-wishlist' ? 'Filter by Generation' :
              collectionType === 'board-games' || collectionType === 'board-games-wishlist' ? 'Filter by Category' :
              collectionType === 'movies' || collectionType === 'movie-wishlist' ? 'Filter by Format' :
              collectionType === 'music' || collectionType === 'music-wishlist' ? 'Filter by Format' :
              collectionType === 'books' || collectionType === 'book-wishlist' ? 'Filter by Format' :
              collectionType === 'trading-cards' || collectionType === 'trading-cards-wishlist' ? 'Filter by Game' :
              collectionType === 'manga' || collectionType === 'manga-wishlist' ? 'Filter by Demographic' :
              collectionType === 'comics' || collectionType === 'comics-wishlist' ? 'Filter by Publisher' :
              'Filter by Platform'}
            statusFilterValue={filterStatus}
            handleStatusFilterChange={(value) => {
              setFilterStatus(value);
            }}
            statusFilterOptions={
              isWishlist
                ? []
                : collectionType === 'games'
                ? [
                    { value: 'not-started', label: 'Not Played' },
                    { value: 'in-progress', label: 'Played (Not Completed)' },
                    { value: 'completed', label: 'Completed' }
                  ]
                : collectionType === 'movies'
                ? [
                    { value: 'not-started', label: 'Not Watched' },
                    { value: 'completed', label: 'Watched' }
                  ]
                : collectionType === 'music'
                ? [
                    { value: 'not-started', label: 'Not Listened' },
                    { value: 'completed', label: 'Listened' }
                  ]
                : collectionType === 'lego'
                ? [
                    { value: 'not-started', label: 'Not Built' },
                    { value: 'completed', label: 'Built' }
                  ]
                : []
            }
            sortValue={sortValue}
            handleSortChange={handleSortChange}
            showFavouritesOnly={showFavouritesOnly}
            onToggleFavouritesFilter={() => setShowFavouritesOnly(prev => !prev)}
            showSoldOnly={showSoldOnly}
            onToggleSoldFilter={!isWishlist ? () => setShowSoldOnly(prev => !prev) : undefined}
            showBacklogOnly={showBacklogOnly}
            onToggleBacklogFilter={!isWishlist && featureFlags.enableBacklog === true && !noBacklogDNFTypes.includes(baseCollectionType) ? () => setShowBacklogOnly(prev => !prev) : undefined}
            showDNFOnly={showDNFOnly}
            onToggleDNFFilter={!isWishlist && featureFlags.enableDNF === true && !noBacklogDNFTypes.includes(baseCollectionType) ? () => setShowDNFOnly(prev => !prev) : undefined}
            onScanBarcode={['game', 'console', 'movie', 'book'].includes(finalItemType) ? handleOpenBarcodeScanner : undefined}
          />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ mt: 4 }}>
            {error}
          </Typography>
        ) : filteredItems.length === 0 && !showSearchResults ? (
          <Typography align="center" sx={{ mt: 4 }}>
            No {collectionType} found. Use the search to add some!
          </Typography>
        ) : (
          !showSearchResults && (
            <>
              {/* Featured Carousel: show most recent 10 items by createdAt */}
              {(() => {
                const byDateDesc = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                const featuredItems = [...filteredItems].sort(byDateDesc).slice(0, 10);
                const proxyIfNeeded = (url) => {
                  if (!url) return '';
                  if (url.includes('mangadex.org') || url.includes('comicvine.gamespot.com')) {
                    return `/api/proxy-image?url=${encodeURIComponent(url)}`;
                  }
                  return url;
                };
                const getImageUrl = (item) => {
                  if (finalItemType === 'console') return item.imageUrl || item.processedImageUrl || item.platformLogo;
                  if (finalItemType === 'manga' || finalItemType === 'comic') {
                    return item.processedImageUrl || proxyIfNeeded(item.imageUrl);
                  }
                  return item.imageUrl || item.set_img_url;
                };
                const getItemDetailUrl = (item) => {
                  if (finalItemType === 'game') return `/item/game/${item._id}`;
                  if (finalItemType === 'console') return `/item/console/${item._id}`;
                  if (finalItemType === 'lego') return `/item/lego/${item._id}`;
                  if (finalItemType === 'movie') return `/item/movie/${item._id}`;
                  if (finalItemType === 'music') return `/item/music/${item._id}`;
                  if (finalItemType === 'book') return `/item/book/${item._id}`;
                  if (finalItemType === 'board-game') return `/item/board-game/${item._id}`;
                  if (finalItemType === 'trading-card') return `/item/trading-card/${item._id}`;
                  if (finalItemType === 'manga') return `/item/manga/${item._id}`;
                  if (finalItemType === 'comic') return `/item/comic/${item._id}`;
                  return '#';
                };
                return (
                  <FeaturedCarousel
                    items={featuredItems}
                    getImageUrl={getImageUrl}
                    getItemName={(i) => i.name}
                    getItemDetailUrl={getItemDetailUrl}
                    onItemClick={(item) => navigate(getItemDetailUrl(item), { state: { item, fromWishlist: isWishlist } })}
                    title={"Recently Added"}
                  />
                );
              })()}
              {!isWishlist && (
                <SuggestionCarousel collectionType={baseCollectionType} />
              )}
              {renderView()}
              {/* Sentinel for infinite scroll */}
              {visibleCount < filteredItems.length && (
                <Box ref={sentinelRef} sx={{ height: 1, mt: 2 }} />
              )}
            </>
          )
        )}

        {showSearchResults && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Search Results</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowSearchResults(false);
                  setSearchQuery('');
                  setSearchResults([]);
                  setFilteredItems(sortItems(applyClientFilters(getFilteredItems()), sortValue));
                }}
              >
                Clear Results
              </Button>
            </Box>
            {customSearchComponent ? (
              customSearchComponent({
                onAddItem: handleAddItem,
                initialQuery: searchQuery,
                hideSearchBox: false,
                itemType: finalItemType
              })
            ) : (
              <SearchResults
                results={sortItems((baseCollectionType === 'games' || baseCollectionType === 'game') ? getFilteredSearchResults() : searchResults, sortValue)}
                loading={searchLoading}
                error={searchError}
                onAddItem={handleAddItem}
                onLoadMore={loadMoreResults}
                hasMoreResults={hasMoreResults}
                itemType={finalItemType}
                isWishlist={isWishlist}
                renderProps={renderProps}
              />
            )}
          </>
        )}

        <EditDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          item={selectedItem}
          onSave={handleSaveEdit}
          itemType={finalItemType}
          fields={editFields}
          isWishlist={isWishlist}
        />
        </Box>

        <Dialog open={barcodeDialogOpen} onClose={handleBarcodeDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Scan Barcode</DialogTitle>
          <DialogContent>
            {/* Camera view — hidden (height 0) when a result is showing */}
            <Box sx={{ width: '100%', height: barcodeDialogPhase === 'scanning' || barcodeDialogPhase === 'looking_up' ? 'auto' : 0, overflow: 'hidden', minHeight: barcodeDialogPhase === 'scanning' || barcodeDialogPhase === 'looking_up' ? 240 : 0 }}>
              <BarcodeScanner
                onDetected={handleBarcodeDetected}
                active={barcodeDialogPhase === 'scanning' || barcodeDialogPhase === 'looking_up'}
                deviceId={selectedCameraId || undefined}
              />
            </Box>

            {/* Camera selector — only show when more than one camera available and scanning */}
            {availableCameras.length > 1 && (barcodeDialogPhase === 'scanning' || barcodeDialogPhase === 'looking_up') && (
              <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <InputLabel>Camera</InputLabel>
                <Select
                  value={selectedCameraId}
                  onChange={(e) => handleCameraChange(e.target.value)}
                  label="Camera"
                >
                  <MenuItem value="">Default (rear)</MenuItem>
                  {availableCameras.map(cam => (
                    <MenuItem key={cam.deviceId} value={cam.deviceId}>
                      {cam.label || `Camera ${cam.deviceId.slice(0, 8)}…`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {barcodeLastDetected && (barcodeDialogPhase === 'looking_up') && (
              <Box sx={{ fontSize: '0.875rem', mt: 1.5, color: 'text.secondary' }}>
                Detected: {barcodeLastDetected}
              </Box>
            )}

            {barcodeDialogPhase === 'looking_up' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={18} />
                <Box sx={{ fontSize: '0.875rem' }}>Looking up barcode…</Box>
              </Box>
            )}

            {/* Found — mini result card */}
            {barcodeDialogPhase === 'found' && barcodeLookupResult && (() => {
              const inCollection = items.some(it =>
                String(it.name || it.title || '').toLowerCase().trim() === barcodeLookupResult.cleanedTitle.toLowerCase().trim()
              );
              const img = barcodeLookupResult.rawItem?.image;
              const detectedPlatform = barcodeLookupResult.rawItem?.meta?.platform || '';
              const provider = barcodeLookupResult.provider || '';
              
              // For games, show platform selector
              const showPlatformSelector = finalItemType === 'game';
              const platformOptions = ['PC (Microsoft Windows)', 'PlayStation 5', 'PlayStation 4', 'PlayStation 3', 'PlayStation 2', 'PlayStation', 'Xbox Series X|S', 'Xbox One', 'Xbox 360', 'Xbox', 'Nintendo Switch', 'Wii U', 'Wii', 'Nintendo 64'];
              
              return (
                <Card variant="outlined" sx={{ display: 'flex', gap: 1.5, p: 1.5, mt: 2, alignItems: 'flex-start' }}>
                  {img && (
                    <Box
                      component="img"
                      src={img}
                      alt={barcodeLookupResult.cleanedTitle}
                      sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                      {barcodeLookupResult.cleanedTitle}
                    </Typography>
                    
                    {showPlatformSelector ? (
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>Platform</InputLabel>
                        <Select
                          value={barcodeSelectedPlatform || detectedPlatform || filter || ''}
                          onChange={(e) => setBarcodeSelectedPlatform(e.target.value)}
                          label="Platform"
                        >
                          {detectedPlatform && (
                            <MenuItem value={detectedPlatform}>{detectedPlatform} (detected)</MenuItem>
                          )}
                          {platformOptions.map(p => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : detectedPlatform ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {detectedPlatform}
                      </Typography>
                    ) : null}
                    
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                      {provider && (
                        <Chip label={provider} size="small" variant="outlined" />
                      )}
                      {inCollection && (
                        <Chip label="Already in collection" size="small" color="success" />
                      )}
                    </Box>
                  </Box>
                </Card>
              );
            })()}

            {/* Not found — inline search box */}
            {barcodeDialogPhase === 'not_found' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {barcodeLookupError || 'No product found for that barcode'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Search by title"
                  value={barcodeNotFoundSearch}
                  onChange={(e) => setBarcodeNotFoundSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && barcodeNotFoundSearch.trim()) handleBarcodeManualSearch(); }}
                  sx={{ mt: 1.5 }}
                  autoFocus
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setBarcodeLookupError('');
                setBarcodeLookupLoading(false);
                setBarcodeNotFoundSearch('');
                setBarcodeLookupResult(null);
                setBarcodeDialogPhase('scanning');
                setBarcodeSelectedPlatform('');
                barcodeLookupInFlightRef.current = false;
              }}
            >
              Scan again
            </Button>

            {barcodeDialogPhase === 'found' && (
              <Button variant="contained" onClick={handleBarcodeUseResult}>
                Add to collection
              </Button>
            )}

            {barcodeDialogPhase === 'not_found' && (
              <Button
                variant="contained"
                onClick={() => handleBarcodeManualSearch()}
                disabled={!barcodeNotFoundSearch.trim()}
              >
                Search
              </Button>
            )}

            <Button onClick={handleBarcodeDialogClose}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          itemType={finalItemType}
          item={itemToDelete}
          itemName={itemToDelete?.name || ''}
        />

        {/* Item Detail Drawer */}
        <ItemDetailDrawer
          open={detailDrawerOpen}
          onClose={() => setDetailDrawerOpen(false)}
          item={selectedDetailItem}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRate={handleRate}
          onToggleFavourite={!isWishlist ? handleToggleFavourite : undefined}
          collectionType={collectionType}
        />
      </Container>
    </div>
  );
};

export default CollectionPage;
